import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Music, Wand2, Scissors, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import SoundLibraryDrawer from "@/components/plyx/SoundLibraryDrawer";
import { applyPlyxBranding } from "@/lib/plyxVideoDownload";

/**
 * PlyxStudio — `/plyx/studio`
 *
 * Lightweight in-browser video editor. Lets a creator:
 *   • Pick a clip from device OR pull the one they just shot on /plyx/cam
 *   • Trim start/end with a draggable slider
 *   • Speed up / slow down (0.5x..2x)
 *   • Apply CSS filter (vivid / b&w / vintage / dreamy / cinematic)
 *   • Pick a soundtrack from the Plyx Sound Library (mutes original audio)
 *   • Preview, then export → /plyx/upload with the edit baked in
 *
 * Implementation is pure browser APIs (no FFmpeg.wasm) — we render the
 * decoded frames to a canvas and capture with MediaRecorder, mixing the
 * source audio (or picked music) via Web Audio API. This keeps the
 * bundle tiny and works offline.
 */

const FILTERS = [
  { id: "none",      label: "None",      css: "none" },
  { id: "vivid",     label: "Vivid",     css: "saturate(1.4) contrast(1.1)" },
  { id: "bw",        label: "B&W",       css: "grayscale(1) contrast(1.05)" },
  { id: "vintage",   label: "Vintage",   css: "sepia(0.5) saturate(0.85) contrast(0.95)" },
  { id: "cinematic", label: "Cinematic", css: "contrast(1.18) saturate(0.92) brightness(0.97)" },
  { id: "dreamy",    label: "Dreamy",    css: "blur(0.4px) saturate(1.15) brightness(1.05) contrast(0.95)" },
  { id: "sunset",    label: "Sunset",    css: "sepia(0.25) saturate(1.25) brightness(1.05) hue-rotate(-12deg)" },
];

const SPEEDS = [
  { v: 0.5, label: "0.5x" },
  { v: 0.75, label: "0.75x" },
  { v: 1, label: "1x" },
  { v: 1.5, label: "1.5x" },
  { v: 2, label: "2x" },
];

const PlyxStudio = () => {
  const navigate = useNavigate();
  const { user } = usePlyxAuth();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [src, setSrc] = useState("");
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [filter, setFilter] = useState("none");
  const [sound, setSound] = useState(null);
  const [showSounds, setShowSounds] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportedBlob, setExportedBlob] = useState(null);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Pick a video file"); return; }
    if (f.size > 80 * 1024 * 1024) { toast.error("Max 80 MB"); return; }
    const url = URL.createObjectURL(f);
    setSrc(url);
    setExportedBlob(null);
  };

  const onLoaded = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    setTrimStart(0);
    setTrimEnd(v.duration || 0);
    v.playbackRate = speed;
  };

  // Live preview reflects speed + filter + trim.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = speed;
  }, [speed, src]);

  // Hard-clip to trim window during playback.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (v.currentTime > trimEnd) { v.currentTime = trimStart; }
      if (v.currentTime < trimStart) { v.currentTime = trimStart; }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [trimStart, trimEnd, src]);

  const exportClip = async () => {
    const v = videoRef.current;
    if (!v) return;
    setExporting(true);
    try {
      // Set up offscreen canvas.
      const w = v.videoWidth || 720;
      const h = v.videoHeight || 1280;
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.filter = FILTERS.find((f) => f.id === filter)?.css || "none";

      // Audio source — Web Audio mix.
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = audioCtx.createMediaStreamDestination();
      let sourceAudio = null;

      if (sound?.audio_url) {
        // Picked soundtrack — fetch + decode + play in loop.
        try {
          const ab = await fetch(sound.audio_url, { mode: "cors" }).then((r) => r.arrayBuffer());
          const buf = await audioCtx.decodeAudioData(ab);
          sourceAudio = audioCtx.createBufferSource();
          sourceAudio.buffer = buf;
          sourceAudio.loop = true;
          sourceAudio.connect(dest);
          sourceAudio.start();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("plyx-studio sound fetch failed, falling back to original audio", err);
        }
      }
      if (!sourceAudio) {
        // Original video audio.
        const elNode = audioCtx.createMediaElementSource(v);
        elNode.connect(dest);
        elNode.connect(audioCtx.destination);
      }

      const canvasStream = canvas.captureStream(30);
      dest.stream.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm;codecs=vp8,opus";
      const recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 4_000_000 });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      const stopPromise = new Promise((resolve) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
      });

      // Drive playback through the trim window at chosen speed.
      v.muted = !!sound;  // mute originals if user picked external music
      v.playbackRate = speed;
      v.currentTime = trimStart;
      await v.play();
      recorder.start(250);

      const draw = () => {
        if (v.currentTime >= trimEnd || v.ended || v.paused) {
          recorder.stop();
          v.pause();
          try { sourceAudio?.stop(); } catch { /* ignore */ }
          return;
        }
        ctx.filter = FILTERS.find((f) => f.id === filter)?.css || "none";
        ctx.drawImage(v, 0, 0, w, h);
        requestAnimationFrame(draw);
      };
      requestAnimationFrame(draw);

      const blob = await stopPromise;
      setExportedBlob(blob);
      toast.success("Edit ready! Preview below.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("plyx-studio export failed", e);
      toast.error("Export failed — try a shorter clip");
    } finally { setExporting(false); }
  };

  const handleUseClip = () => {
    if (!exportedBlob) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem("plyx_studio_blob", reader.result);
        sessionStorage.setItem("plyx_studio_caption", "");
        navigate("/plyx/upload?from=studio");
      } catch {
        toast.error("Clip too large — download it instead");
      }
    };
    reader.readAsDataURL(exportedBlob);
  };

  const handleDownload = async () => {
    if (!exportedBlob) return;
    // iter-119t: All videos leaving Plyx must carry the
    // "plyx @handle" watermark + outro. Run the client-side brander
    // before saving. Falls back to the raw clip if the browser can't
    // bake the watermark.
    const handle = user?.handle || "creator";
    const brandingToast = toast.loading("Adding Plyx watermark…");
    let blobToSave = exportedBlob;
    let ext = "webm";
    try {
      const result = await applyPlyxBranding(exportedBlob, {
        mediaType: "video",
        handle,
        prerollSec: 1.0,
        outroSec: 1.4,
      });
      if (result?.ok && result.blob) {
        blobToSave = result.blob;
        ext = result.ext || "mp4";
        toast.success("Saved · plyx @" + handle + " watermark + outro applied", { id: brandingToast });
      } else {
        toast.message("Saved · your browser couldn't watermark, raw clip downloaded", { id: brandingToast });
      }
    } catch {
      toast.message("Saved · raw clip downloaded", { id: brandingToast });
    }
    const url = URL.createObjectURL(blobToSave);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plyx-${handle}-${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <Wand2 className="w-12 h-12 mb-3 text-white/40"/>
        <div className="text-lg font-bold mb-1">Sign in to edit</div>
        <div className="text-sm text-white/60 mb-4">Plyx Studio is for signed-in creators.</div>
        <button onClick={() => navigate("/plyx/login?next=/plyx/studio")} className="bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl px-5 py-2" data-testid="plyx-studio-login-cta">Log in</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-28" data-testid="plyx-studio-root">
      <div className="flex items-center justify-between px-4 pt-3">
        <button onClick={() => navigate(-1)} className="rounded-full bg-white/10 hover:bg-white/20 p-2" data-testid="plyx-studio-close">
          <X className="w-5 h-5"/>
        </button>
        <div className="text-xs font-bold tracking-widest text-white/60">PLYX STUDIO</div>
        <div className="w-9"/>
      </div>

      {!src ? (
        <div className="px-6 py-10">
          <div className="rounded-3xl border-2 border-dashed border-white/20 px-6 py-10 text-center" data-testid="plyx-studio-empty">
            <Scissors className="w-10 h-10 mx-auto text-white/30 mb-3"/>
            <div className="font-bold mb-1">Pick a clip to edit</div>
            <div className="text-sm text-white/60 mb-4">Trim, speed, filter & swap the soundtrack.</div>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={handlePick} data-testid="plyx-studio-file-input"/>
            <button onClick={() => fileRef.current?.click()} className="bg-white text-black font-bold rounded-xl px-5 py-2" data-testid="plyx-studio-pick-btn">Choose video</button>
            <div className="mt-3 text-xs text-white/40">or use a clip from <button onClick={() => navigate("/plyx/cam")} className="text-[#FFCC2E] underline">Plyx Cam</button></div>
          </div>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          <div className="rounded-2xl overflow-hidden bg-zinc-900 aspect-[9/16] sm:aspect-video relative">
            <video
              ref={videoRef}
              src={src}
              onLoadedMetadata={onLoaded}
              className="w-full h-full object-contain"
              style={{ filter: FILTERS.find((f) => f.id === filter)?.css }}
              controls
              playsInline
              data-testid="plyx-studio-preview"
            />
          </div>

          {/* Trim slider */}
          <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3" data-testid="plyx-studio-trim">
            <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
              <span>Trim</span>
              <span>{trimStart.toFixed(1)}s – {trimEnd.toFixed(1)}s · {(trimEnd - trimStart).toFixed(1)}s</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-white/40 mb-0.5">Start</div>
                <input type="range" min="0" max={duration || 0} step="0.1" value={trimStart}
                  onChange={(e) => setTrimStart(Math.min(parseFloat(e.target.value), trimEnd - 0.5))}
                  className="w-full" data-testid="plyx-studio-trim-start"/>
              </div>
              <div>
                <div className="text-[10px] text-white/40 mb-0.5">End</div>
                <input type="range" min="0" max={duration || 0} step="0.1" value={trimEnd}
                  onChange={(e) => setTrimEnd(Math.max(parseFloat(e.target.value), trimStart + 0.5))}
                  className="w-full" data-testid="plyx-studio-trim-end"/>
              </div>
            </div>
          </div>

          {/* Speed */}
          <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3" data-testid="plyx-studio-speed">
            <div className="text-[11px] text-white/60 mb-2">Speed</div>
            <div className="flex gap-2">
              {SPEEDS.map((s) => (
                <button
                  key={s.v} onClick={() => setSpeed(s.v)}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-lg transition ${speed === s.v ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                  data-testid={`plyx-studio-speed-${s.v}x`.replace(".", "")}
                >{s.label}</button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3" data-testid="plyx-studio-filters">
            <div className="text-[11px] text-white/60 mb-2">Filter</div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {FILTERS.map((f) => (
                <button
                  key={f.id} onClick={() => setFilter(f.id)}
                  className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === f.id ? "bg-[#FFCC2E] text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                  data-testid={`plyx-studio-filter-${f.id}`}
                >{f.label}</button>
              ))}
            </div>
          </div>

          {/* Soundtrack picker */}
          <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3" data-testid="plyx-studio-music">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] text-white/60">Soundtrack</div>
              <button onClick={() => setShowSounds(true)} className="text-xs font-bold text-[#22D3EE] flex items-center gap-1" data-testid="plyx-studio-open-sounds">
                <Music className="w-3.5 h-3.5"/> {sound ? "Change" : "Pick a sound"}
              </button>
            </div>
            {sound ? (
              <div className="flex items-center gap-2 text-sm">
                {sound.thumbnail_emoji && <span className="text-xl">{sound.thumbnail_emoji}</span>}
                <div className="flex-1 truncate"><b>{sound.title}</b> <span className="text-white/40">· {sound.artist || sound.genre}</span></div>
                <button onClick={() => setSound(null)} className="text-white/40 hover:text-white text-xs" data-testid="plyx-studio-clear-sound">Remove</button>
              </div>
            ) : (
              <div className="text-xs text-white/40">Original audio will play unless you pick a soundtrack.</div>
            )}
          </div>

          <button
            onClick={exportClip}
            disabled={exporting}
            className="w-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-2xl py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="plyx-studio-export"
          >
            {exporting ? <><Loader2 className="w-4 h-4 animate-spin"/> Exporting…</> : <><Sparkles className="w-4 h-4"/> Export edit</>}
          </button>
        </div>
      )}

      {/* Result modal */}
      {exportedBlob && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center px-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/15 p-5 w-full max-w-sm" data-testid="plyx-studio-result">
            <div className="font-bold text-lg mb-1">Edit ready</div>
            <div className="text-xs text-white/60 mb-3">Use on Plyx or download.</div>
            <video src={URL.createObjectURL(exportedBlob)} className="w-full rounded-xl mb-4" controls playsInline/>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={handleDownload} className="bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl py-2 text-sm flex items-center justify-center gap-1" data-testid="plyx-studio-download">
                <Download className="w-3.5 h-3.5"/> Download
              </button>
              <button onClick={handleUseClip} className="bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2 text-sm" data-testid="plyx-studio-use">Use on Plyx →</button>
            </div>
            <button onClick={() => setExportedBlob(null)} className="w-full mt-2 text-xs text-white/40 hover:text-white" data-testid="plyx-studio-discard">Discard & re-edit</button>
          </div>
        </div>
      )}

      <SoundLibraryDrawer
        open={showSounds}
        onClose={() => setShowSounds(false)}
        onPick={(s) => setSound(s)}
        selectedId={sound?.sound_id}
      />
    </div>
  );
};

export default PlyxStudio;
